import { TransactionItemOptionsQuery } from '@/context/services/transaction_items/Detail/TransactionItemDetail.service';
import { useTransactionItemDetailEdit } from '@/features/transaction_items/Detail/context/TransactionItemDetailEdit';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  Form,
  FormInput,
  FormSelect,
  Label,
  LabelProps,
  SearchConditions,
} from '@moneyforward/ap-frontend-components';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import styles from './Search.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  onSearch: (query: TransactionItemOptionsQuery) => void;
  isDisabled: boolean;
};

export type SearchType = {
  keyword: string;
  visibility: number | undefined;
};

export const Search: FC<Props> = memo(({ onSearch, isDisabled }) => {
  const { t } = useTranslation();
  const { isEdit } = useTransactionItemDetailEdit();
  const methods = useForm<SearchType>({
    defaultValues: {
      keyword: '',
      visibility: undefined,
    },
    mode: 'all',
  });

  const { watch } = methods;
  const values = watch();
  const inputs = useMemo(() => {
    const { keyword, visibility } = values;
    return {
      keyword,
      is_visible:
        visibility === null || visibility === undefined
          ? undefined
          : visibility === 1,
    };
  }, [values]);

  const onSubmit = useCallback(() => {
    onSearch(inputs);
  }, [inputs, onSearch]);

  const onClear = useCallback(() => {
    methods.reset({ keyword: '', visibility: undefined });
    onSearch({});
  }, [methods, onSearch]);

  const searchInputProps: {
    label: LabelProps;
    inputProps: { search: boolean };
  } = useMemo(() => {
    return {
      label: {
        text: t('transaction_items.search.keyword'),
        tooltipIcon: 'help',
        tooltip: t('transaction_items.search.keyword_tooltip'),
      },
      inputProps: {
        search: true,
        disabled: isDisabled || isEdit,
        placeholder: t('transaction_items.search.keyword_placeholder'),
      },
    };
  }, [t, isDisabled, isEdit]);

  const visibilitySelectProps = useMemo(() => {
    return {
      label: {
        text: t('transaction_items.detail.options.enabled_disabled'),
      },
      selectProps: {
        options: [
          {
            label: t('transaction_items.detail.options.enabled'),
            value: 1,
          },
          {
            label: t('transaction_items.detail.options.disabled'),
            value: 0,
          },
        ],
        disabled: isDisabled || isEdit,
      },
    };
  }, [t, isDisabled, isEdit]);

  const search = useLocation().search;
  useMemo(() => {
    const keyword = new URLSearchParams(search).get('keyword');
    const visibility = new URLSearchParams(search).get('is_visible');
    const isVisible = visibility === 'true' ? 1 : 0;
    if (methods.getValues().keyword !== keyword && keyword) {
      methods.reset({ ...methods.getValues(), keyword });
    }
    if (visibility && methods.getValues().visibility !== isVisible) {
      methods.reset({
        ...methods.getValues(),
        visibility: visibility === 'true' ? 1 : 0,
      });
    }
  }, [search, methods]);

  return (
    <div className={cx(styles['container'])}>
      <Label
        text={t('transaction_items.search.label')}
        labelClassName={styles['label']}
      />
      <div>
        <SearchConditions>
          <Form<SearchType> formMethod={methods} onSubmit={onSubmit}>
            <Row
              align='bottom'
              wrap
              className={cx(styles['search-input-container'])}
            >
              <Col className={cx(styles['search-input'])}>
                <FormInput
                  {...searchInputProps}
                  name='keyword'
                  control={methods.control}
                  inputId='keyword'
                  testId='keyword'
                />
              </Col>
              <Col className={cx(styles['search-input'])}>
                <FormSelect<SearchType>
                  control={methods.control}
                  name='visibility'
                  inputId='visibility'
                  testId='visibility'
                  {...visibilitySelectProps}
                />
              </Col>
              <div className={cx(styles['search-button-container'])}>
                <ButtonV2
                  isTertiary
                  size='sm'
                  onClick={onClear}
                  isDisabled={isDisabled || isEdit}
                >
                  {t('clear')}
                </ButtonV2>
                <ButtonV2
                  isSecondary
                  size='sm'
                  type='submit'
                  isDisabled={isDisabled || isEdit}
                >
                  {t('search')}
                </ButtonV2>
              </div>
            </Row>
          </Form>
        </SearchConditions>
      </div>
    </div>
  );
});
Search.displayName = 'Search';
